import { useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { usePosts } from '@/hooks/usePosts';
import { useHeroSettings } from '@/hooks/useHeroSettings';
import { useAboutSettings } from '@/hooks/useAboutSettings';
import { useContactSettings } from '@/hooks/useContactSettings';
import { usePrivacySettings } from '@/hooks/usePrivacySettings';
import { useCategories } from '@/hooks/useCategories';
import { useSiteSettings } from '@/hooks/useSiteSettings';
import { useNewsletterSettings } from '@/hooks/useNewsletterSettings';
import { Post } from '@/hooks/usePosts';
import PostEditor from './components/PostEditor';
import HeroEditor from './components/HeroEditor';
import AboutEditor from './components/AboutEditor';
import ContactEditor from './components/ContactEditor';
import PrivacyEditor from './components/PrivacyEditor';
import CategoryEditor from './components/CategoryEditor';
import DelaySettingsEditor from './components/DelaySettingsEditor';
import NewsletterEditor from './components/NewsletterEditor';
import SiteSettingsEditor from './components/SiteSettingsEditor';
import Dashboard from './components/Dashboard';

type AdminView = 'list' | 'create' | 'edit' | 'hero';
type AdminSection = 'dashboard' | 'posts' | 'categories' | 'site' | 'hero' | 'newsletter' | 'about' | 'contact' | 'privacy' | 'delay';

export default function AdminPage() {
  const [session, setSession] = useState(() => supabase.auth.getSession());
  const [authMode, setAuthMode] = useState<'login' | 'signup'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [authLoading, setAuthLoading] = useState(false);

  const [section, setSection] = useState<AdminSection>('dashboard');
  const [view, setView] = useState<AdminView>('list');
  const [editingPost, setEditingPost] = useState<Post | undefined>(undefined);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  const { posts, addPost, updatePost, deletePost, error: postsError } = usePosts();
  const { settings: heroSettings, saveSettings: saveHeroSettings } = useHeroSettings();
  const { settings: aboutSettings, saveSettings: saveAboutSettings } = useAboutSettings();
  const { settings: contactSettings, saveSettings: saveContactSettings } = useContactSettings();
  const { settings: privacySettings, saveSettings: savePrivacySettings } = usePrivacySettings();
  const { settings: siteSettings, saveSettings: saveSiteSettings } = useSiteSettings();
  const { settings: newsletterSettings, saveSettings: saveNewsletterSettings } = useNewsletterSettings();
  const { getCategoryLabel } = useCategories();

  // Check for existing session
  supabase.auth.onAuthStateChange((_event, newSession) => {
    if (newSession) setSession(newSession);
    else setSession(null);
  });

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError('');
    setAuthLoading(true);

    try {
      if (authMode === 'signup') {
        const { error } = await supabase.auth.signUp({ email, password });
        if (error) throw error;
        setAuthError('Conta criada! Verifique seu email para confirmar, depois faça login.');
        setAuthMode('login');
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
      }
    } catch (err) {
      setAuthError(err instanceof Error ? err.message : 'Erro de autenticação');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setSession(null);
  };

  const handleSavePost = async (post: Post) => {
    setSaving(true);
    if (editingPost) {
      await updatePost(post.id, post);
    } else {
      await addPost(post);
    }
    setSaving(false);
    setView('list');
    setEditingPost(undefined);
  };

  const handleEdit = (post: Post) => {
    setEditingPost(post);
    setView('edit');
    setSection('posts');
  };

  const handleDelete = async (id: string) => {
    setSaving(true);
    await deletePost(id);
    setSaving(false);
    setDeleteConfirm(null);
  };

  const handleNavClick = (itemId: AdminSection) => {
    setSection(itemId);
    if (itemId !== 'posts') setView('list');
    setMobileSidebarOpen(false);
  };

  const navItems: { id: AdminSection; label: string; icon: string; badge?: number }[] = [
    { id: 'dashboard', label: 'Dashboard', icon: 'ri-dashboard-line' },
    { id: 'posts', label: 'Artigos', icon: 'ri-article-line', badge: posts.length },
    { id: 'categories', label: 'Categorias', icon: 'ri-price-tag-3-line' },
    { id: 'site', label: 'Identidade', icon: 'ri-settings-3-line' },
    { id: 'hero', label: 'Banner Hero', icon: 'ri-layout-top-line' },
    { id: 'newsletter', label: 'Newsletter', icon: 'ri-mail-send-line' },
    { id: 'about', label: 'Sobre nós', icon: 'ri-information-line' },
    { id: 'contact', label: 'Fale Conosco', icon: 'ri-chat-smile-2-line' },
    { id: 'privacy', label: 'Privacidade', icon: 'ri-shield-check-line' },
    { id: 'delay', label: 'Delay', icon: 'ri-time-line' },
  ];

  const isEditing = section === 'posts' && (view === 'create' || view === 'edit');

  // ── Sidebar content shared between desktop and mobile
  const sidebarContent = (
    <>
      <nav className="space-y-1">
        {navItems.map(item => (
          <button
            key={item.id}
            onClick={() => handleNavClick(item.id)}
            className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-bold cursor-pointer transition-colors ${
              section === item.id && !isEditing
                ? 'bg-primary-100 text-primary-600'
                : 'text-foreground-600 hover:bg-background-100'
            }`}
          >
            <span className="flex items-center gap-2.5">
              <i className={`${item.icon} text-base`}></i>
              {item.label}
            </span>
            {item.badge !== undefined && (
              <span className="text-xs bg-background-200 text-foreground-500 px-1.5 py-0.5 rounded-full">
                {item.badge}
              </span>
            )}
          </button>
        ))}
      </nav>

      {/* Error display */}
      {postsError && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-600 text-xs">{postsError}</p>
        </div>
      )}

      {/* Stats mini */}
      <div className="mt-6 border-t border-background-200 pt-5 space-y-3">
        {[
          { label: 'Publicados', value: posts.filter(p => p.published).length, color: 'text-green-600' },
          { label: 'Em destaque', value: posts.filter(p => p.featured).length, color: 'text-primary-500' },
          { label: 'Rascunhos', value: posts.filter(p => !p.published).length, color: 'text-foreground-400' },
          { label: 'Total', value: posts.length, color: 'text-foreground-700' },
        ].map(s => (
          <div key={s.label} className="flex items-center justify-between">
            <span className="text-xs text-foreground-500">{s.label}</span>
            <span className={`text-sm font-bold ${s.color}`}>{s.value}</span>
          </div>
        ))}
      </div>
    </>
  );

  /* ─── Login Screen ─── */
  if (!session) {
    return (
      <div className="min-h-screen bg-background-100 flex items-center justify-center px-4">
        <div className="bg-background-50 rounded-xl p-8 w-full max-w-sm border border-background-200">
          <Link to="/" className="flex items-center gap-2 mb-8 cursor-pointer">
            <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-primary-500 flex items-center justify-center">
              <i className="ri-heart-fill text-primary-500 text-lg"></i>
            </div>
            <div>
              <div className="font-heading font-bold text-primary-500 text-sm leading-none">miaudecasa</div>
              <div className="text-foreground-400 text-xs">Área Administrativa</div>
            </div>
          </Link>

          <h1 className="font-heading font-bold text-foreground-950 text-2xl mb-2">
            {authMode === 'login' ? 'Entrar' : 'Criar conta'}
          </h1>
          <p className="text-foreground-500 text-sm mb-6">
            {authMode === 'login'
              ? 'Acesse a área administrativa do blog.'
              : 'Crie seu acesso de administrador.'}
          </p>

          <form onSubmit={handleAuth} className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-foreground-700 uppercase tracking-wide mb-1.5">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="seu@email.com"
                required
                className="w-full border border-background-300 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-primary-400 bg-background-50"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-foreground-700 uppercase tracking-wide mb-1.5">
                Senha
              </label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Sua senha..."
                required
                className="w-full border border-background-300 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-primary-400 bg-background-50"
              />
            </div>
            {authError && (
              <p className={`text-xs ${authError.includes('Criada') ? 'text-green-600' : 'text-red-500'}`}>
                {authError}
              </p>
            )}
            <button
              type="submit"
              disabled={authLoading}
              className="w-full bg-primary-500 hover:bg-primary-600 disabled:opacity-60 text-white font-bold text-sm py-2.5 rounded-md transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
            >
              {authLoading && <i className="ri-loader-4-line animate-spin"></i>}
              {authMode === 'login' ? 'Entrar' : 'Criar conta'}
            </button>
          </form>

        </div>
      </div>
    );
  }

  /* ─── Admin Panel ─── */
  return (
    <div className="min-h-screen bg-background-100 font-body flex flex-col">

      {/* Top Header */}
      <header className="w-full bg-background-50 border-b border-background-200 flex-shrink-0">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Mobile hamburger */}
            <button
              onClick={() => setMobileSidebarOpen(true)}
              className="md:hidden w-9 h-9 flex items-center justify-center rounded-lg text-foreground-600 hover:bg-background-100 cursor-pointer transition-colors"
            >
              <i className="ri-menu-line text-xl"></i>
            </button>
            <Link to="/" className="flex items-center gap-2 cursor-pointer">
              <i className="ri-heart-fill text-primary-500 text-xl"></i>
              <span className="font-heading font-bold text-foreground-950 text-sm hidden sm:block">
                miaudecasa
              </span>
            </Link>
            <span className="text-background-300 text-lg hidden md:block">|</span>
            <span className="text-foreground-600 text-sm font-bold hidden md:block">
              Painel Admin
            </span>
          </div>
          <div className="flex items-center gap-3 md:gap-4">
            <span className="text-xs text-foreground-400 hidden lg:block">{session.user?.email}</span>
            <Link
              to="/"
              target="_blank"
              className="text-xs text-foreground-500 hover:text-primary-500 flex items-center gap-1 cursor-pointer whitespace-nowrap transition-colors"
            >
              <i className="ri-external-link-line"></i>
              <span className="hidden sm:inline">Ver site</span>
            </Link>
            <button
              onClick={handleLogout}
              className="text-xs text-foreground-500 hover:text-red-500 flex items-center gap-1 cursor-pointer whitespace-nowrap transition-colors"
            >
              <i className="ri-logout-box-line"></i>
              <span className="hidden sm:inline">Sair</span>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      {mobileSidebarOpen && (
        <div className="md:hidden fixed inset-0 z-40">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setMobileSidebarOpen(false)}
          />
          {/* Sidebar panel */}
          <div className="absolute left-0 top-0 bottom-0 w-64 bg-background-50 border-r border-background-200 p-5 overflow-y-auto shadow-lg animate-slide-in-left">
            <div className="flex items-center justify-between mb-6">
              <span className="font-heading font-bold text-foreground-950 text-sm">Navegação</span>
              <button
                onClick={() => setMobileSidebarOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-500 hover:bg-background-100 cursor-pointer transition-colors"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
            {sidebarContent}
          </div>
        </div>
      )}

      <div className="flex flex-1 max-w-7xl w-full mx-auto px-4 md:px-6 py-8 gap-6">

        {/* Desktop Sidebar */}
        <aside className="w-52 flex-shrink-0 hidden md:block">
          {sidebarContent}
        </aside>

        {/* Main content */}
        <main className="flex-1 min-w-0">

          {/* Saving overlay */}
          {saving && (
            <div className="fixed inset-0 bg-black/20 z-40 flex items-center justify-center">
              <div className="bg-background-50 rounded-xl px-6 py-4 shadow-lg flex items-center gap-3">
                <i className="ri-loader-4-line animate-spin text-primary-500 text-xl"></i>
                <span className="text-sm text-foreground-700">Salvando...</span>
              </div>
            </div>
          )}

          {/* ── DASHBOARD ── */}
          {section === 'dashboard' && (
            <Dashboard />
          )}

          {/* ── DELAY EDITOR ── */}
          {section === 'delay' && (
            <DelaySettingsEditor
              settings={siteSettings}
              onSave={saveSiteSettings}
            />
          )}

          {/* ── SITE EDITOR ── */}
          {section === 'site' && (
            <SiteSettingsEditor
              settings={siteSettings}
              onSave={saveSiteSettings}
            />
          )}

          {/* ── NEWSLETTER EDITOR ── */}
          {section === 'newsletter' && (
            <NewsletterEditor
              settings={newsletterSettings}
              onSave={saveNewsletterSettings}
            />
          )}

          {/* ── HERO EDITOR ── */}
          {section === 'hero' && (
            <HeroEditor
              settings={heroSettings}
              onSave={saveHeroSettings}
            />
          )}

          {/* ── ABOUT EDITOR ── */}
          {section === 'about' && (
            <AboutEditor
              settings={aboutSettings}
              onSave={saveAboutSettings}
            />
          )}

          {/* ── CONTACT EDITOR ── */}
          {section === 'contact' && (
            <ContactEditor
              settings={contactSettings}
              onSave={saveContactSettings}
            />
          )}

          {/* ── PRIVACY EDITOR ── */}
          {section === 'privacy' && (
            <PrivacyEditor
              settings={privacySettings}
              onSave={savePrivacySettings}
            />
          )}

          {/* ── CATEGORY EDITOR ── */}
          {section === 'categories' && (
            <CategoryEditor />
          )}

          {/* ── POSTS SECTION ── */}
          {section === 'posts' && (
            <>
              {isEditing ? (
                <PostEditor
                  post={editingPost}
                  onSave={handleSavePost}
                  onCancel={() => { setView('list'); setEditingPost(undefined); }}
                />
              ) : (
                <>
                  {/* Post List Header */}
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="font-heading font-bold text-foreground-950 text-xl md:text-2xl">
                      Artigos
                    </h2>
                    <button
                      onClick={() => { setEditingPost(undefined); setView('create'); }}
                      className="flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-bold px-4 py-2 rounded-md transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-add-line"></i> Novo Artigo
                    </button>
                  </div>

                  {/* Stats row */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6 md:mb-8">
                    {[
                      { label: 'Total', value: posts.length, icon: 'ri-article-line' },
                      { label: 'Publicados', value: posts.filter(p => p.published).length, icon: 'ri-eye-line' },
                      { label: 'Em destaque', value: posts.filter(p => p.featured).length, icon: 'ri-star-line' },
                      { label: 'Rascunhos', value: posts.filter(p => !p.published).length, icon: 'ri-draft-line' },
                    ].map(stat => (
                      <div key={stat.label} className="bg-background-50 rounded-lg border border-background-200 p-3 md:p-4">
                        <div className="flex items-center gap-2 mb-1">
                          <i className={`${stat.icon} text-primary-500 text-base md:text-lg`}></i>
                          <span className="text-xl md:text-2xl font-bold text-foreground-950">{stat.value}</span>
                        </div>
                        <span className="text-foreground-500 text-xs">{stat.label}</span>
                      </div>
                    ))}
                  </div>

                  {/* Error */}
                  {postsError && (
                    <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
                      <i className="ri-error-warning-line text-red-500 flex-shrink-0"></i>
                      <div>
                        <p className="text-red-700 text-sm font-bold">Erro ao carregar dados</p>
                        <p className="text-red-500 text-xs">{postsError}</p>
                      </div>
                    </div>
                  )}

                  {/* Post Table */}
                  <div className="bg-background-50 rounded-lg border border-background-200 overflow-hidden">
                    {posts.length === 0 && !postsError ? (
                      <div className="py-16 text-center text-foreground-500">
                        <i className="ri-article-line text-4xl mb-3 block"></i>
                        <p className="text-sm">Nenhum artigo ainda. Crie o primeiro!</p>
                      </div>
                    ) : (
                      <>
                        {/* Desktop Table */}
                        <div className="hidden md:block overflow-x-auto">
                          <table className="w-full">
                            <thead>
                              <tr className="border-b border-background-200 text-left">
                                <th className="px-4 py-3 text-xs font-bold text-foreground-500 uppercase tracking-wide">Artigo</th>
                                <th className="px-4 py-3 text-xs font-bold text-foreground-500 uppercase tracking-wide">Categoria</th>
                                <th className="px-4 py-3 text-xs font-bold text-foreground-500 uppercase tracking-wide hidden lg:table-cell">Status</th>
                                <th className="px-4 py-3 text-xs font-bold text-foreground-500 uppercase tracking-wide hidden lg:table-cell">Destaque</th>
                                <th className="px-4 py-3 text-xs font-bold text-foreground-500 uppercase tracking-wide">Ações</th>
                              </tr>
                            </thead>
                            <tbody>
                              {posts.map(post => (
                                <tr key={post.id} className="border-b border-background-100 hover:bg-background-100/60 transition-colors">
                                  <td className="px-4 py-3">
                                    <div className="flex items-center gap-3">
                                      {post.image && (
                                        <div className="w-12 h-10 rounded overflow-hidden flex-shrink-0">
                                          <img src={post.image} alt={post.title} className="w-full h-full object-cover object-top" />
                                        </div>
                                      )}
                                      <div>
                                        <p className="font-bold text-foreground-950 text-sm line-clamp-1">{post.title}</p>
                                        <p className="text-foreground-400 text-xs">{post.readTime}</p>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-4 py-3">
                                    <span className="text-primary-500 text-xs font-bold uppercase tracking-wide">
                                      {post.categories?.slice(0,2).map(id => getCategoryLabel(id)).join(', ')}
                                    </span>
                                  </td>
                                  <td className="px-4 py-3 hidden lg:table-cell">
                                    <span className={`text-xs font-bold px-2 py-1 rounded-full ${
                                      post.published ? 'bg-green-100 text-green-700' : 'bg-background-200 text-foreground-500'
                                    }`}>
                                      {post.published ? 'Publicado' : 'Rascunho'}
                                    </span>
                                  </td>
                                  <td className="px-4 py-3 hidden lg:table-cell">
                                    <button
                                      onClick={async () => {
                                        await updatePost(post.id, { featured: !post.featured });
                                      }}
                                      title={post.featured ? 'Remover destaque' : 'Destacar no carrossel'}
                                      className={`w-6 h-6 flex items-center justify-center cursor-pointer transition-colors ${
                                        post.featured ? 'text-primary-500' : 'text-background-300 hover:text-primary-300'
                                      }`}
                                    >
                                      <i className={post.featured ? 'ri-star-fill' : 'ri-star-line'}></i>
                                    </button>
                                  </td>
                                  <td className="px-4 py-3">
                                    <div className="flex items-center gap-1.5">
                                      <Link
                                        to={`/post/${post.slug}`}
                                        target="_blank"
                                        className="w-7 h-7 flex items-center justify-center text-foreground-400 hover:text-primary-500 transition-colors cursor-pointer"
                                        title="Ver artigo"
                                      >
                                        <i className="ri-eye-line text-sm"></i>
                                      </Link>
                                      <button
                                        onClick={() => handleEdit(post)}
                                        className="w-7 h-7 flex items-center justify-center text-foreground-400 hover:text-primary-500 transition-colors cursor-pointer"
                                        title="Editar"
                                      >
                                        <i className="ri-edit-line text-sm"></i>
                                      </button>
                                      <button
                                        onClick={() => setDeleteConfirm(post.id)}
                                        className="w-7 h-7 flex items-center justify-center text-foreground-400 hover:text-red-500 transition-colors cursor-pointer"
                                        title="Excluir"
                                      >
                                        <i className="ri-delete-bin-line text-sm"></i>
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        {/* Mobile Card List */}
                        <div className="md:hidden divide-y divide-background-100">
                          {posts.map(post => (
                            <div key={post.id} className="p-4 hover:bg-background-100/60 transition-colors">
                              <div className="flex items-start gap-3">
                                {post.image && (
                                  <div className="w-14 h-12 rounded overflow-hidden flex-shrink-0">
                                    <img src={post.image} alt={post.title} className="w-full h-full object-cover object-top" />
                                  </div>
                                )}
                                <div className="flex-1 min-w-0">
                                  <p className="font-bold text-foreground-950 text-sm line-clamp-2 mb-1">{post.title}</p>
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-primary-500 text-xs font-bold uppercase">
                                      {post.categories?.slice(0,1).map(id => getCategoryLabel(id)).join(', ')}
                                    </span>
                                    <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${
                                      post.published ? 'bg-green-100 text-green-700' : 'bg-background-200 text-foreground-500'
                                    }`}>
                                      {post.published ? 'Publicado' : 'Rascunho'}
                                    </span>
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-1 mt-3">
                                <Link
                                  to={`/post/${post.slug}`}
                                  target="_blank"
                                  className="flex-1 flex items-center justify-center gap-1 text-xs border border-background-300 rounded-md py-1.5 text-foreground-500 hover:text-primary-500 hover:border-primary-300 cursor-pointer transition-colors"
                                >
                                  <i className="ri-eye-line text-xs"></i> Ver
                                </Link>
                                <button
                                  onClick={() => handleEdit(post)}
                                  className="flex-1 flex items-center justify-center gap-1 text-xs border border-background-300 rounded-md py-1.5 text-foreground-500 hover:text-primary-500 hover:border-primary-300 cursor-pointer transition-colors"
                                >
                                  <i className="ri-edit-line text-xs"></i> Editar
                                </button>
                                <button
                                  onClick={() => setDeleteConfirm(post.id)}
                                  className="flex items-center justify-center w-9 h-8 rounded-md border border-background-300 text-foreground-400 hover:border-red-300 hover:text-red-500 cursor-pointer transition-colors"
                                >
                                  <i className="ri-delete-bin-line text-xs"></i>
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </>
              )}
            </>
          )}
        </main>
      </div>

      {/* Delete Confirm Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
          <div className="bg-background-50 rounded-xl p-6 max-w-sm w-full border border-background-200">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-red-100 mb-4">
              <i className="ri-delete-bin-line text-xl text-red-500"></i>
            </div>
            <h3 className="font-bold text-foreground-950 text-lg mb-2">Excluir artigo?</h3>
            <p className="text-foreground-500 text-sm mb-6">
              Esta ação não pode ser desfeita. O artigo será removido permanentemente.
            </p>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 py-2 text-sm border border-background-300 rounded-md text-foreground-600 hover:bg-background-100 cursor-pointer whitespace-nowrap transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                disabled={saving}
                className="flex-1 py-2 text-sm bg-red-500 hover:bg-red-600 disabled:opacity-60 text-white rounded-md cursor-pointer whitespace-nowrap font-bold transition-colors"
              >
                {saving ? 'Excluindo...' : 'Excluir'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile slide-in animation */}
      <style>{`
        @keyframes slide-in-left {
          from { transform: translateX(-100%); }
          to { transform: translateX(0); }
        }
        .animate-slide-in-left {
          animation: slide-in-left 0.25s ease-out;
        }
      `}</style>
    </div>
  );
}
